import React from 'react';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { PermissionAwareInput, PermissionAwareSelect } from '../../../contexts/PermissionAccessContext';
import { YES_NO_OPTIONS, fromDateInput, toDateInput } from './unitForm';

/**
 * Field primitives shared by the Unit drawer tabs (Driver, Truck). Every input is a
 * PermissionAware* control or a shared picker, so they disable themselves below edit
 * level inside the tab's PermissionAccessProvider.
 */

export const labelClass = 'block text-xs font-medium text-muted mb-0.5';
export const inputClass = 'block w-full rounded-md border border-rule-strong bg-field px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800';
export const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

export const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className = '' }) => (
  <div className={className}>
    <span className={labelClass}>{label}</span>
    {children}
  </div>
);

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="rounded-md border border-rule p-2">
    <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</legend>
    <div className="grid grid-cols-2 gap-2">{children}</div>
  </fieldset>
);

export const TextField: React.FC<{
  label: string; value: unknown; onChange: (v: string) => void; type?: string; className?: string; readOnly?: boolean; maxLength?: number;
}> = ({ label, value, onChange, type = 'text', className, readOnly, maxLength }) => (
  <Field label={label} className={className}>
    <PermissionAwareInput
      type={type}
      aria-label={label}
      className={inputClass}
      value={str(value)}
      readOnly={readOnly}
      disabled={readOnly}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

/**
 * Dollar amount over a `decimal(10,2)` column (e.g. `truck.cmv`, V1
 * "Coverage/Market Value"). Native number input, two decimals, never negative; the wire
 * value is the input's string so an untouched '80000.00' round-trips unchanged.
 */
export const MoneyField: React.FC<{ label: string; value: unknown; onChange: (v: string) => void; className?: string; readOnly?: boolean }> = ({ label, value, onChange, className, readOnly }) => (
  <Field label={label} className={className}>
    <PermissionAwareInput
      type="number"
      step="0.01"
      min="0"
      inputMode="decimal"
      aria-label={label}
      className={inputClass}
      value={str(value)}
      readOnly={readOnly}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

export const YesNoField: React.FC<{ label: string; value: unknown; onChange: (v: string) => void; className?: string; readOnly?: boolean }> = ({ label, value, onChange, className, readOnly }) => (
  <Field label={label} className={className}>
    <PermissionAwareSelect aria-label={label} className={inputClass} value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {YES_NO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </PermissionAwareSelect>
  </Field>
);

export const DateField: React.FC<{
  label: string; value: unknown; onChange: (v: string) => void; className?: string; portalContainer: HTMLElement | null; readOnly?: boolean;
}> = ({ label, value, onChange, className, portalContainer, readOnly }) => (
  <Field label={label} className={className}>
    <DateTimePicker
      mode="date"
      selectionType="single"
      inputVariant="modal"
      labelType="none"
      ariaLabel={label}
      autoApply
      autoFlip
      portalContainer={portalContainer}
      disabled={readOnly}
      value={toDateInput(value)}
      onChange={(d) => onChange(fromDateInput(d instanceof Date ? d : null))}
    />
  </Field>
);

/**
 * Native datetime-local input over a DB datetime column ('YYYY-MM-DD HH:MM:SS'); the value
 * is converted by the caller's `toInput` / `fromInput` so the form keeps the DB format.
 */
export const DateTimeField: React.FC<{
  label: string; value: string; onChange: (inputValue: string) => void; className?: string; readOnly?: boolean;
}> = ({ label, value, onChange, className, readOnly }) => (
  <Field label={label} className={className}>
    <PermissionAwareInput type="datetime-local" aria-label={label} className={inputClass} value={value} readOnly={readOnly} disabled={readOnly}
      onChange={(e) => onChange(e.target.value)} />
  </Field>
);

interface SelectFieldProps {
  label: string; value: unknown; onChange: (v: string) => void; options: { value: string; label: string }[]; className?: string; search?: boolean; readOnly?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options, className, search, readOnly }) => (
  <Field label={label} className={className}>
    <SingleSelect ariaLabel={label} value={str(value)} onChange={onChange} options={options} size="sm" usePortal enableSearch={search} showClearOption clearOptionLabel="—" disabled={readOnly} />
  </Field>
);

/**
 * Select over a recno-backed lookup (region, drivertype, truckmake, lienholderrecno, ...).
 * The column is an INT, and V1 stores 0 for "none" — so a cleared pick is reported as
 * `0`, never `''` (which the backend would try to bind into an INT column).
 */
export const LookupSelectField: React.FC<Omit<SelectFieldProps, 'onChange'> & { onChange: (v: string | number) => void }> = ({ onChange, ...rest }) => (
  <SelectField {...rest} onChange={(v) => onChange(v === '' ? 0 : v)} />
);

/**
 * Masked SSN / Tax ID with a gated Edit control: `value === null` shows the mask.
 * The input keeps whatever the user types (dashes, spaces) for display, but `onChange`
 * receives digits only — that is what is sent, and what the backend validates (9 digits).
 */
/** Read-only placeholder for a PII value the server withheld (payroll-sensitive < 2). */
export const HiddenField: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <Field label={label} className={className}>
    <span className="text-sm text-muted" aria-label={`${label} hidden`}>•••• hidden</span>
  </Field>
);

export const MaskedSsnField: React.FC<{
  label: string; last4: unknown; value: string | null; onChange: (v: string | null) => void; canEdit: boolean; className?: string;
}> = ({ label, last4, value, onChange, canEdit, className }) => {
  const [text, setText] = React.useState('');
  React.useEffect(() => { if (value === null) setText(''); }, [value]);
  return (
    <Field label={label} className={className}>
      {value === null ? (
        <div className="flex items-center gap-2">
          <span className="text-sm">{str(last4) ? `•••• ${str(last4)}` : '—'}</span>
          {canEdit && (
            <button type="button" onClick={() => onChange('')} className="text-xs font-medium text-accent-text hover:underline" aria-label={`Edit ${label}`}>
              Edit
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <PermissionAwareInput aria-label={label} className={inputClass} value={text} maxLength={11} placeholder="###-##-####" autoComplete="off"
            onChange={(e) => { setText(e.target.value); onChange(e.target.value.replace(/\D/g, '')); }} />
          <button type="button" onClick={() => onChange(null)} className="text-xs text-muted hover:underline">Cancel</button>
        </div>
      )}
    </Field>
  );
};
